import React, { useEffect, useState } from "react";
import { Crown, Check, Loader2, Sparkles } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";

/**
 * Public-facing tier picker for a creator page.
 * Shows up to 3 tiers (free tier has none → component renders nothing).
 * Highlights the current viewer's active subscription with a "Current plan" badge.
 */
export default function CreatorSubTiers({ creatorId, creatorName }) {
  const { user } = useAuth();
  const [tiers, setTiers] = useState([]);
  const [status, setStatus] = useState({ subscribed: false });
  const [busyTier, setBusyTier] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get(`/creators/${creatorId}/subscription-tiers`);
      setTiers(data);
    } catch { /* ignore */ }
    if (user) {
      try {
        const { data } = await api.get(`/creator/subscription-status/${creatorId}`);
        setStatus(data);
      } catch { /* ignore */ }
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [creatorId, user?.user_id]);

  const subscribe = async (tier) => {
    if (!user) { toast.info("Sign in to subscribe"); return; }
    if (user.user_id === tier.creator_id) { toast.error("Can't subscribe to your own channel"); return; }
    setBusyTier(tier.id);
    try {
      const { data } = await api.post(`/creator/subscribe/${tier.id}`, { origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) { toast.error(formatApiError(e)); setBusyTier(null); }
  };

  if (!tiers.length) return null;

  return (
    <section data-testid="creator-sub-tiers" className="px-4 sm:px-8 md:px-12 mt-12">
      <div className="flex items-center gap-2">
        <Sparkles className="w-4 h-4 text-tv-accent" />
        <div className="text-xs uppercase tracking-[0.3em] text-tv-accent font-bold">Fan Club</div>
      </div>
      <h2 className="font-display font-black text-3xl sm:text-4xl text-white mt-2 tracking-tighter">
        Back {creatorName} with a monthly membership
      </h2>
      <p className="text-white/60 text-sm mt-2 max-w-2xl">
        Direct support that funds new episodes, live broadcasts, and merch drops. 85% goes straight to the creator.
      </p>

      {status.subscribed && (
        <div data-testid="sub-active-badge" className="mt-4 inline-flex items-center gap-2 bg-emerald-500/10 border border-emerald-500/30 rounded-full px-4 py-2 text-emerald-300 text-sm font-semibold">
          <Check className="w-4 h-4" />
          Active member · <span className="text-emerald-200">{status.tier_name}</span> · renews {new Date(status.expires_at).toLocaleDateString()}
        </div>
      )}

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-6">
        {tiers.map((t, i) => {
          const isCurrent = status.subscribed && status.tier_id === t.id;
          const isBusy = busyTier === t.id;
          const highlight = tiers.length >= 2 && i === 1; // feature the middle tier
          return (
            <div
              key={t.id}
              data-testid={`sub-tier-${t.id}`}
              className={`relative rounded-2xl border p-5 flex flex-col ${
                highlight
                  ? "border-tv-accent/60 bg-gradient-to-br from-tv-accent/10 via-white/[0.02] to-transparent shadow-xl shadow-tv-accent/10"
                  : "border-white/10 bg-white/[0.03] hover:border-white/20"
              }`}
            >
              {highlight && (
                <div className="absolute -top-3 left-5 bg-tv-accent text-black text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full">
                  Popular
                </div>
              )}
              <div className="flex items-center gap-2">
                <Crown className={`w-4 h-4 ${highlight ? "text-tv-accent" : "text-white/60"}`} />
                <div className="font-display font-bold text-white text-lg tracking-tight">{t.name}</div>
              </div>
              <div className="mt-3 flex items-baseline gap-1">
                <span className="font-display font-black text-3xl text-white">${t.price_monthly_usd.toFixed(2)}</span>
                <span className="text-white/50 text-sm">/month</span>
              </div>
              {t.perks?.length > 0 && (
                <ul className="mt-4 space-y-1.5 flex-1">
                  {t.perks.map((p, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-white/85 text-sm">
                      <Check className={`w-4 h-4 mt-0.5 shrink-0 ${highlight ? "text-tv-accent" : "text-emerald-400"}`} />
                      <span>{p}</span>
                    </li>
                  ))}
                </ul>
              )}
              <button
                data-testid={`sub-tier-subscribe-${t.id}`}
                disabled={isBusy || isCurrent}
                onClick={() => subscribe(t)}
                className={`mt-5 font-bold rounded-full py-2.5 px-4 transition text-sm flex items-center justify-center gap-2 ${
                  isCurrent
                    ? "bg-emerald-500/20 text-emerald-300 cursor-default border border-emerald-500/30"
                    : highlight
                      ? "bg-tv-accent hover:bg-yellow-400 text-black"
                      : "bg-white/10 hover:bg-white/20 text-white"
                }`}
              >
                {isBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                {isCurrent ? "Your current plan" : isBusy ? "Redirecting…" : `Subscribe · $${t.price_monthly_usd.toFixed(2)}/mo`}
              </button>
            </div>
          );
        })}
      </div>
    </section>
  );
}
